import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, X, Loader2, Calendar, Package, User, Phone, Trash2, Tag } from 'lucide-react';

const Bookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewbookings`, {
                headers: { authorization: `Bearer ${token}` } 
            });
            setBookings(res.data.bookings);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this booking?")) return;

        setDeletingId(id);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/deletebooking/${id}`, {
                headers: { authorization: `Bearer ${token}` }
            });
            setBookings(bookings.filter(booking => booking._id !== id));
        } catch (err) {
            console.error("Delete error:", err);
            alert("Failed to delete booking");
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) return (
        <div className="p-10 flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="animate-spin text-orange-500 mb-2" size={32} />
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Fetching Bookings</p>
        </div>
    );

    return (
        <div className="p-4 md:p-6 lg:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black uppercase italic leading-none">
                        Fleet <span className="text-orange-500">Bookings</span>
                    </h1>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                        {bookings.length} Active Reservations
                    </p>
                </div>
            </div>

            {/* --- DESKTOP TABLE VIEW --- */}
            <div className="hidden lg:block bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-[0.2em]">
                        <tr>
                            <th className="p-5">SL</th>
                            <th className="p-5">Product Details</th>
                            <th className="p-5">Customer</th>
                            <th className="p-5">Contact</th>
                            <th className="p-5 text-center">Date</th>
                            <th className="p-5 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium text-slate-700 divide-y divide-slate-50">
                        {bookings.map((item, index) => (
                            <tr key={item._id} className="hover:bg-orange-50/30 transition-all group">
                                <td className="p-5 text-slate-300 font-black italic">{String(index + 1).padStart(2, '0')}</td>
                                <td className="p-5">
                                    <div className="flex flex-col">
                                        <span className="text-orange-500 font-black uppercase text-[10px] tracking-widest mb-1">
                                            {item.productId?.categoryId?.categoryName || "Fleet"}
                                        </span>
                                        <span className="text-slate-900 font-black uppercase italic tracking-tight text-sm">
                                            {item.productId?.productName || "N/A"}
                                        </span>
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center gap-2 font-bold text-slate-800">
                                        <User size={14} className="text-slate-400" /> {item.customerName}
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center gap-2 font-bold text-slate-600">
                                        <Phone size={14} className="text-orange-500" /> {item.customerPhone}
                                    </div>
                                </td>
                                <td className="p-5 text-center">
                                    <div className="inline-flex items-center gap-2 bg-slate-100 px-3 py-1 rounded-full text-slate-500 text-[10px] font-bold uppercase">
                                        <Calendar size={10} />
                                        {new Date(item.createdAt).toLocaleDateString('en-GB')}
                                    </div>
                                </td>
                                <td className="p-5">
                                    <div className="flex items-center justify-center gap-2">
                                        <button className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all">
                                            <Check size={18} strokeWidth={3} />
                                        </button>
                                        <button 
                                            disabled={deletingId === item._id}
                                            onClick={() => handleDelete(item._id)}
                                            className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-all disabled:opacity-50"
                                        >
                                            {deletingId === item._id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={18} />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- MOBILE/TABLET CARD VIEW --- */}
            <div className="lg:hidden space-y-4">
                {bookings.map((item, index) => (
                    <div key={item._id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 px-3 py-1 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest rounded-bl-xl">
                            {new Date(item.createdAt).toLocaleDateString('en-GB')}
                        </div>
                        
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-1">
                                <Tag size={12} className="text-orange-500" />
                                <span className="text-[9px] font-black text-orange-500 uppercase tracking-widest">
                                    {item.productId?.categoryId?.categoryName || "General Fleet"}
                                </span>
                            </div>
                            <h3 className="text-sm font-black text-slate-900 uppercase italic">{item.productId?.productName || "Unknown Model"}</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center gap-3 text-slate-700">
                                <User size={14} className="text-slate-400" />
                                <span className="text-xs font-bold uppercase tracking-tight">{item.customerName}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-700">
                                <Phone size={14} className="text-orange-500" />
                                <span className="text-xs font-bold">{item.customerPhone}</span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button className="flex-1 flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">
                                <Check size={14} strokeWidth={3} /> Approve
                            </button>
                            <button 
                                onClick={() => handleDelete(item._id)}
                                disabled={deletingId === item._id}
                                className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-600 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest"
                            >
                                {deletingId === item._id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {bookings.length === 0 && (
                <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
                    <Package className="mx-auto text-slate-200 mb-4" size={48} />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No active bookings found</p>
                </div>
            )}
        </div>
    );
};

export default Bookings;